import styles from "./StaffItem.module.scss";
import { IStaffItem } from "../../../models/Staff";
import React, { useState } from "react";
import { setProfileImage } from "../../../helper/imagePaths";
import { useTranslation } from "react-i18next";
import StaffPopup from "../StaffPopup/StaffPopup";
import ConfirmPopup from "../../../components/ConfirmPopup/ConfirmPopup";
import { useNavigate } from "react-router-dom";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { openSuccessMessage } from "../../../store/message/messageSlice";
import { handleError } from "../../../helper/errorHandler";
import { useStaffMutations } from "../../../api/mutations";

interface IStaffItemProps {
  data: IStaffItem;
  activeTab: "admin" | "support";
}

const StaffItem: React.FC<IStaffItemProps> = ({ data, activeTab }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [showCreatePopup, setShowCreatePopup] = useState<{
    show: boolean;
    activeTab: "admin" | "support";
  }>({ show: false, activeTab });
  const [showConfirmPopup, setShowConfirmPopup] = useState<boolean>(false);

  const { deleteAdmin, deleteSupport } = useStaffMutations();

  const handleMenu = () => {
    setShowMenu(!showMenu);
  };

  const handleViewOnMenu = () => {
    setShowCreatePopup({ show: true, activeTab });
  };

  const handleDeleteOnMenu = () => {
    setShowConfirmPopup(true);
  };

  const clickYesOnConfirmPopup = () => {
    if (activeTab === "admin") {
      deleteAdmin(data.id, {
        onSuccess: () => {
          redirect(true, activeTab);
          dispatch(openSuccessMessage(t("ProfileDeletedSuccessfully")));
        },
        onError: (e) => handleError(e, dispatch, t),
      });
    } else {
      deleteSupport(data.id, {
        onSuccess: () => {
          redirect(true, activeTab);
          dispatch(openSuccessMessage(t("ProfileDeletedSuccessfully")));
        },
        onError: (e) => handleError(e, dispatch, t),
      });
    }
    setShowConfirmPopup(false);
  };

  const clickNoOnConfirmPopup = () => {
    setShowConfirmPopup(false);
  };

  const redirect = (shouldUpdate: boolean, activeTab: "admin" | "support") => {
    navigate(`/staff/${activeTab}s/?shouldUpdate=${shouldUpdate}`);
  };

  return (
    <>
      <div className={styles.item}>
        <div onClick={handleMenu} className={styles.more}>
          <div className={`${styles.menu} ${showMenu && styles.active}`}>
            <p className={styles.menuItem} onClick={handleViewOnMenu}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.3866 7.99995C10.3866 9.31995 9.31995 10.3866 7.99995 10.3866C6.67995 10.3866 5.61328 9.31995 5.61328 7.99995C5.61328 6.67995 6.67995 5.61328 7.99995 5.61328C9.31995 5.61328 10.3866 6.67995 10.3866 7.99995Z"
                  stroke="#404040"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.9999 13.5133C10.3532 13.5133 12.5466 12.1266 14.0732 9.72665C14.6732 8.78665 14.6732 7.20665 14.0732 6.26665C12.5466 3.86665 10.3532 2.47998 7.9999 2.47998C5.64656 2.47998 3.45323 3.86665 1.92656 6.26665C1.32656 7.20665 1.32656 8.78665 1.92656 9.72665C3.45323 12.1266 5.64656 13.5133 7.9999 13.5133Z"
                  stroke="#404040"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t("View")}
            </p>
            <p className={`${styles.menuItem} ${styles.delete}`} onClick={handleDeleteOnMenu}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.6667 4.66663C12.4899 4.66663 12.3203 4.73686 12.1953 4.86189C12.0702 4.98691 12 5.15648 12 5.33329V12.794C11.9809 13.1311 11.8293 13.447 11.5783 13.6729C11.3273 13.8988 10.9973 14.0163 10.66 14H5.34C5.00273 14.0163 4.67266 13.8988 4.42167 13.6729C4.17069 13.447 4.01912 13.1311 4 12.794V5.33329C4 5.15648 3.92976 4.98691 3.80474 4.86189C3.67971 4.73686 3.51014 4.66663 3.33333 4.66663C3.15652 4.66663 2.98695 4.73686 2.86193 4.86189C2.7369 4.98691 2.66667 5.15648 2.66667 5.33329V12.794C2.6857 13.4848 2.97772 14.1399 3.47879 14.6159C3.97985 15.0918 4.64912 15.3498 5.34 15.3333H10.66C11.3509 15.3498 12.0201 15.0918 12.5212 14.6159C13.0223 14.1399 13.3143 13.4848 13.3333 12.794V5.33329C13.3333 5.15648 13.2631 4.98691 13.1381 4.86189C13.013 4.73686 12.8435 4.66663 12.6667 4.66663ZM13.3333 2.66663H10.6667V1.33329C10.6667 1.15648 10.5964 0.986912 10.4714 0.861888C10.3464 0.736864 10.1768 0.666626 10 0.666626H6C5.82319 0.666626 5.65362 0.736864 5.5286 0.861888C5.40357 0.986912 5.33333 1.15648 5.33333 1.33329V2.66663H2.66667C2.48986 2.66663 2.32029 2.73686 2.19526 2.86189C2.07024 2.98691 2 3.15648 2 3.33329C2 3.5101 2.07024 3.67967 2.19526 3.8047C2.32029 3.92972 2.48986 3.99996 2.66667 3.99996H13.3333C13.5101 3.99996 13.6797 3.92972 13.8047 3.8047C13.9298 3.67967 14 3.5101 14 3.33329C14 3.15648 13.9298 2.98691 13.8047 2.86189C13.6797 2.73686 13.5101 2.66663 13.3333 2.66663ZM6.66667 2.66663V1.99996H9.33333V2.66663H6.66667Z"
                  fill="#D93131"
                />
                <path
                  d="M7.33333 11.3333V6.66667C7.33333 6.48986 7.2631 6.32029 7.13807 6.19526C7.01305 6.07024 6.84348 6 6.66667 6C6.48986 6 6.32029 6.07024 6.19526 6.19526C6.07024 6.32029 6 6.48986 6 6.66667V11.3333C6 11.5101 6.07024 11.6797 6.19526 11.8047C6.32029 11.9298 6.48986 12 6.66667 12C6.84348 12 7.01305 11.9298 7.13807 11.8047C7.2631 11.6797 7.33333 11.5101 7.33333 11.3333ZM10 11.3333V6.66667C10 6.48986 9.92976 6.32029 9.80474 6.19526C9.67971 6.07024 9.51014 6 9.33333 6C9.15652 6 8.98695 6.07024 8.86193 6.19526C8.7369 6.32029 8.66667 6.48986 8.66667 6.66667V11.3333C8.66667 11.5101 8.7369 11.6797 8.86193 11.8047C8.98695 11.9298 9.15652 12 9.33333 12C9.51014 12 9.67971 11.9298 9.80474 11.8047C9.92976 11.6797 10 11.5101 10 11.3333Z"
                  fill="#D93131"
                />
              </svg>
              {t("Delete")}
            </p>
          </div>
        </div>
        <div
          className={styles.profileImage}
          style={{ backgroundImage: `url(${setProfileImage(data.profileImagePath)})` }}
        ></div>
        <h4 className={styles.name}>
          {data.firstname} {data.lastname}
        </h4>
        <p className={styles.number}>
          {data.phoneCountryCode}
          {data.phoneNumber}
        </p>
        <p className={styles.email}>{data.email}</p>
      </div>
      {showCreatePopup.show && (
        <StaffPopup
          activeTab={showCreatePopup.activeTab}
          staffItem={data}
          setShowCreatePopup={setShowCreatePopup}
        />
      )}
      {showConfirmPopup && (
        <ConfirmPopup
          title={t("AreYouSureYouWantToDeleteThisProfile")}
          onClickYes={clickYesOnConfirmPopup}
          onClickNo={clickNoOnConfirmPopup}
        />
      )}
    </>
  );
};

export default StaffItem;
